import { Node } from 'modules/workflows/types';
import {
  FRONTEND_ERROR_FAILURE_CODE,
  FRONTEND_ERROR_FAILURE_MESSAGE,
  TEMPLATE_ERROR_FAILURE_CODE,
  TEMPLATE_ERROR_FAILURE_MESSAGE,
} from './useValidateWorkflow.constants';
import { NodeError, TemplateResponse, ValidationResponse } from './useValidateWorkflow.types';

// Maps template ID to a node ID
export const getTemplateIdMap = (nodes: Node[]) => {
  const templateIdMap = new Map();
  nodes.forEach((node) => {
    if (node.nodeData?.templateId) templateIdMap.set(node.nodeData?.templateId, node.id);
  });
  return templateIdMap;
};

/**
 * Parses the response from the template validation gql query so that all
 * queries return the same shape.
 *
 * @param resp The response when validating a template
 * @param templateIdMap Map of template ID to node ID
 * @returns An object containing the list of errors.
 */
export const parseTemplateValidationResponse = (
  resp: TemplateResponse,
  templateIdMap: Map<number, number>,
): ValidationResponse => {
  const templateErrors = resp?.data?.validateMultiContent?.errors || [];
  const parsedErrors = templateErrors.reduce((acc: NodeError[], err) => {
    const { key, value } = err;
    if (!value?.length) return acc;
    return [
      ...acc,
      {
        data: { nodeId: templateIdMap.get(key) as number, value, templateId: key },
        message: TEMPLATE_ERROR_FAILURE_MESSAGE,
        code: TEMPLATE_ERROR_FAILURE_CODE,
      },
    ];
  }, []);
  return { errors: parsedErrors, data: resp?.data };
};

/**
 * Parse the response from the frontend validation query so that all queries
 * return the same shape.
 *
 * @param invalidNodes List of ids of nodes that are invalid
 * @returns An object containing the list of errors.
 */
export const parseFrontendValidationResponse = (invalidNodes: string[] = []) => {
  const parsedErrors = invalidNodes.map((nodeId) => ({
    data: { nodeId },
    message: FRONTEND_ERROR_FAILURE_MESSAGE,
    code: FRONTEND_ERROR_FAILURE_CODE,
  }));

  return { errors: parsedErrors };
};

/**
 * Flattens the response from all validation queries into a single list of
 * errors.
 * @param resp The response from all validation queries
 * @returns A list of node errors
 */
export const flattenResponseErrors = (resp: ValidationResponse[]): NodeError[] =>
  resp.reduce(
    (acc: NodeError[], curr) => (curr?.errors?.length ? [...acc, ...curr.errors] : acc),
    [],
  );
